/**
 * A terminal-shaped Markdown renderer.
 *
 * The shell prints `Line[]`, not HTML, so the usual Markdown-to-DOM libraries
 * are useless here — and pulling one in for a few hundred bytes of prose would
 * outweigh the whole terminal. This covers the subset a text file on this site
 * actually uses, and maps it onto the same semantic styles every other command
 * writes with, so a document recolours with the theme like everything else.
 *
 * Fenced blocks are reproduced byte for byte: the ASCII frames in README.md
 * only survive if nothing reflows, trims or re-wraps them. The fence info
 * string names the style to paint the block with (```accent), because art is
 * the common case here and `dim` grey art looks broken.
 */

import { blank, line, link, span, text } from './format'
import type { Line, Span, Style } from './types'

const STYLES: readonly Style[] = [
  'dim',
  'accent',
  'ok',
  'warn',
  'err',
  'dir',
  'exec',
  'link',
  'git',
  'num',
  'key',
  'str',
]

const isStyle = (value: string): value is Style => (STYLES as readonly string[]).includes(value)

/** Rules are drawn to the width of the ASCII frames the documents ship with. */
const RULE_WIDTH = 75

/**
 * Only `https:` and same-origin absolute paths become live targets. A document
 * is operator-editable content, so an unchecked href here would be a stored
 * `javascript:` waiting to happen.
 */
const safeHref = (value: string): string | undefined => {
  if (value.startsWith('/') && !value.startsWith('//')) return value
  try {
    return new URL(value).protocol === 'https:' ? value : undefined
  } catch {
    return undefined
  }
}

/*
 * One alternation, scanned once: `[text](url)`, `![alt](url)`, `` `code` ``,
 * `**strong**`, `*emphasis*`, and bare links. Matching them separately would
 * let an earlier pass rewrite the inside of a later match.
 */
const INLINE =
  /!?\[([^\]\n]*)\]\(([^)\s]+)\)|`([^`\n]+)`|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|(https?:\/\/[^\s<>()[\]]+)/g

/** Splits one paragraph of Markdown into styled spans. */
export function inlineSpans(value: string, base?: Style): Span[] {
  const spans: Span[] = []
  const plain = (piece: string) => {
    if (piece) spans.push(span(piece, base))
  }

  /*
   * A fresh scanner per call rather than the shared literal: emphasis recurses
   * to pick up markup nested inside it, and a nested call would otherwise move
   * the outer loop's `lastIndex` out from under it.
   */
  const scanner = new RegExp(INLINE.source, 'g')

  let index = 0
  for (let match = scanner.exec(value); match; match = scanner.exec(value)) {
    plain(value.slice(index, match.index))
    index = match.index + match[0].length

    const [whole, label, url, code, strong, emphasis, bare] = match
    if (url !== undefined) {
      // An inline image keeps its alt text in the flow; the media itself is
      // attached to the line by the block pass, which owns `Line.media`.
      const href = safeHref(url)
      const caption = label || url
      if (whole.startsWith('!')) plain(caption)
      else if (href) spans.push(link(caption, href))
      else plain(caption)
    } else if (code !== undefined) spans.push(span(code, 'exec'))
    else if (strong !== undefined) spans.push(...inlineSpans(strong, 'key'))
    else if (emphasis !== undefined) spans.push(...inlineSpans(emphasis, base ?? 'dim'))
    else if (bare !== undefined) {
      const href = safeHref(bare)
      if (href) spans.push(link(bare, href))
      else plain(bare)
    }
  }
  plain(value.slice(index))
  return spans.length ? spans : [span('', base)]
}

/** `![alt](src)` alone on a line becomes a picture under the caption. */
const IMAGE_LINE = /^!\[([^\]\n]*)\]\(([^)\s]+)\)$/

const HEADING = /^(#{1,6})\s+(.*)$/
const UNORDERED = /^(\s*)[-*+]\s+(.*)$/
const ORDERED = /^(\s*)(\d+)[.)]\s+(.*)$/
const QUOTE = /^\s*>\s?(.*)$/
const RULE = /^\s*([-*_])\1{2,}\s*$/
const FENCE = /^\s*(?:```|~~~)\s*(\w*)\s*$/

/** Heading level to the style its text is painted with. */
const headingStyle = (level: number): Style => (level === 1 ? 'key' : level === 2 ? 'accent' : 'dir')

export function markdownLines(source: string): Line[] {
  const rows = source.replace(/\r\n?/g, '\n').split('\n')
  const out: Line[] = []

  let fence: { style?: Style } | null = null

  for (const row of rows) {
    const fenced = FENCE.exec(row)

    if (fence) {
      if (fenced) {
        fence = null
        continue
      }
      // Verbatim, including trailing spaces: the frames are padded to width.
      // Flagged as art so the renderer can close the gaps between rows.
      out.push({ ...text(row, fence.style), art: true })
      continue
    }

    if (fenced) {
      const info = fenced[1]
      fence = { style: info && isStyle(info) ? info : undefined }
      continue
    }

    if (!row.trim()) {
      out.push(blank())
      continue
    }

    if (RULE.test(row)) {
      out.push(text('─'.repeat(RULE_WIDTH), 'dim'))
      continue
    }

    const picture = IMAGE_LINE.exec(row.trim())
    if (picture) {
      const src = safeHref(picture[2])
      const alt = picture[1] || 'image'
      if (src) out.push({ ...text(alt, 'dim'), media: { src, alt } })
      else out.push(text(alt, 'dim'))
      continue
    }

    const heading = HEADING.exec(row)
    if (heading) {
      const level = heading[1].length
      out.push(
        line(span(`${heading[1]} `, 'accent'), ...inlineSpans(heading[2], headingStyle(level))),
      )
      continue
    }

    const quote = QUOTE.exec(row)
    if (quote) {
      out.push(line(span('│ ', 'accent'), ...inlineSpans(quote[1], 'dim')))
      continue
    }

    const ordered = ORDERED.exec(row)
    if (ordered) {
      const indent = ' '.repeat(2 + ordered[1].length)
      out.push(
        line(span(indent), span(`${ordered[2]}. `, 'num'), ...inlineSpans(ordered[3])),
      )
      continue
    }

    const unordered = UNORDERED.exec(row)
    if (unordered) {
      const indent = ' '.repeat(2 + unordered[1].length)
      out.push(line(span(indent), span('• ', 'accent'), ...inlineSpans(unordered[2])))
      continue
    }

    out.push(line(...inlineSpans(row)))
  }

  return out
}
